import { cn } from "@/lib/cn";
import { EstrelaDeFavorito } from "@/components/shared/EstrelaDeFavorito";

type Option = {
  id: string;
  label: string;
};

type Props = {
  options: Option[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  disabled?: boolean;
  /** Largura mínima de cada botão — todos ficam do mesmo tamanho dentro do grupo. */
  minWidthPx?: number;
  /**
   * O que estava escolhido ANTES (só na edição do laboratório). Fica marcado em âmbar enquanto não
   * for o escolhido atual, pra dar a comparação "era isto → virou aquilo" na própria grade, sem
   * precisar ler um resumo em outro canto da tela.
   */
  originalId?: string | null;
  /** Com `onFavoritar`, cada botão ganha a estrela da pessoa. */
  favoritos?: Set<string>;
  onFavoritar?: (id: string) => void;
};

/** Grade de botões de seleção única, sempre do mesmo tamanho dentro do grupo — clicar no já selecionado desmarca. */
export function SingleSelectButtons({
  options,
  selectedId,
  onSelect,
  disabled,
  minWidthPx = 108,
  originalId = null,
  favoritos,
  onFavoritar,
}: Props) {
  return (
    <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${minWidthPx}px, 1fr))` }}>
      {options.map((opt) => {
        const active = opt.id === selectedId;
        const ehOriginal = !!originalId && opt.id === originalId && !active;
        return (
          <div key={opt.id} className="group relative">
            <button
              type="button"
              disabled={disabled}
              title={ehOriginal ? `${opt.label} — escolha original` : opt.label}
              onClick={() => onSelect(opt.id)}
              className={cn(
                "flex min-h-8 w-full items-center justify-center rounded-md border py-1 text-center text-[12.5px] font-medium leading-tight transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                onFavoritar ? "pl-2 pr-6" : "px-2",
                active
                  ? "border-brand-300 bg-brand-50 text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
                  : ehOriginal
                    ? "border-amber-400 bg-amber-50 text-amber-800 dark:border-amber-500/50 dark:bg-amber-500/10 dark:text-amber-400"
                    : "border-zinc-200 bg-white text-zinc-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:border-border dark:bg-card dark:text-muted-foreground dark:hover:border-primary/40 dark:hover:bg-primary/10 dark:hover:text-primary",
              )}
            >
              {opt.label}
            </button>
            {onFavoritar && (
              <EstrelaDeFavorito
                ativo={!!favoritos?.has(opt.id)}
                onAlternar={() => onFavoritar(opt.id)}
                className="absolute right-1 top-1/2 -translate-y-1/2"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
